

































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { Route } from "vue-router";
// types
import { UserGroup, User, SelectOption } from "@/services/users/types";
// service methods
import {
    createUserGroup,
    getUserGroupById,
    updateUserGroup
} from "@/services/users/userGroupService";
import { getUserGroupAcl } from "@/services/security/accessControlService";
import { getSetting, setSetting } from "@/services/autoSettings/autoSettingsService";
import { validateForm } from "@/services/validation/validationService";

// components
import HeaderActionButtons from "@/components/layouts/HeaderActionButtons.vue";
import UserGroupMediaLibraryACL from "@/components/users/UserGroupMediaLibraryACL.vue";
import UserGroupDetails from "@/components/users/UserGroupDetails.vue";
import UnsavedFormModal from "@/components/common/modals/UnsavedFormModal.vue";
import UserGroupResourcesACL from "@/components/users/UserGroupResourcesACL.vue";
// types
import { Acl, ResourceType } from "@/services/security/types";
import { FormComponent, FormComponentModel } from "@/services/autoSettings/types";
import UserGroupUsers from "@/components/users/UserGroupUsers.vue";
import { bus } from "@/bus";
import i18n from "@/i18n";

const authenticationModule = namespace("authentication");

@Component<UserGroupForm>({
    beforeRouteLeave(to, from, next) {
        this.localId ? this.localUserGroup.id ? this.onBeforeRouteLeave(to, next) : next()
            : this.onBeforeRouteLeave(to, next);
    },
    components: {
        UserGroupDetails,
        HeaderActionButtons,
        UserGroupMediaLibraryACL,
        UnsavedFormModal,
        UserGroupResourcesACL,
        UserGroupUsers
    }
})
export default class UserGroupForm extends Vue {
    @Prop() private groupId!: string;
    @Prop() private groupName!: string;
    @authenticationModule.Getter("getCurrentUser") currentUser!: User;

    // local state
    group = {
        name: "",
        description: "",
        azureAdGroupId: "",
        acl: [] as Acl[],
        userIds: [] as string[]
    } as UserGroup;

    acl = [] as Acl[];
    componentsData = [] as FormComponentModel[];
    editorProfiles = [] as SelectOption[];
    formState = false;
    resourceType = ResourceType;

    // used for comparing if form have changes
    localUserGroup = {
        name: "",
        description: "",
        azureAdGroupId: "",
        acl: [] as Acl[],
        userIds: [] as string[]
    } as UserGroup;

    nextRoute = "";
    saveClicked = false;
    routeParams = {};
    localId = this.groupId || "";

    // lifecycle hooks
    public created() {
        this.fetchData();
        this.initComponents();
    }

    // Watcher
    firstTime = true as boolean;
    @Watch("$route", { immediate: true, deep: true })
    onUrlChange(newVal: any) {
        if (this.firstTime) {
            this.firstTime = false;
        } else {
            this.fetchData();
        }
    }

    // Computed
    get userGroupDetails(): any {
        return {
            name: this.group.name,
            description: this.group.description,
            azureAdGroupId: this.group.azureAdGroupId
        } as UserGroup;
    }

    get components(): FormComponentModel[] {
        return this.componentsData.sort((x, y) => x.order - y.order);
    }

    get editMode() {
        return !!this.localId;
    }

    get componentsAutoSettingKey() : string {
        let settingsKey = "userGroupFormComponents";
        settingsKey += this.localId ? "-edit" : "-create";
        return settingsKey;
    }

    // functions
    async initComponents() {
        let autoSettingComponents = getSetting(this.componentsAutoSettingKey) as FormComponent[];
        const defaultComponents = [
            {
                componentName: "UserGroupDetails",
                sectionName: i18n.t("common.details"),
                order: 0,
                collapseState: true
            },
            {
                componentName: "UserGroupResourcesACL",
                sectionName: i18n.t("acl.title"),
                order: 1,
                collapseState: true
            },
            {
                componentName: "UserGroupMediaLibraryACL",
                sectionName: i18n.t("acl.access"),
                order: 2,
                collapseState: true
            },
            {
                componentName: "UserGroupUsers",
                sectionName: i18n.t("users.title"),
                order: 3,
                collapseState: true
            }] as FormComponent[];

        if (!autoSettingComponents) {
            autoSettingComponents = await this.initAutoSettings(defaultComponents);
        } else {
            // Check if saved auto setting components has same component and if not then set to default components.
            var hasSameComponents = this.lodash.isEqual(
                this.lodash.sortBy(autoSettingComponents.map(component => component.componentName)),
                this.lodash.sortBy(defaultComponents.map(component => component.componentName))
            );
            if (hasSameComponents) {
                await this.updateComponents(defaultComponents, autoSettingComponents);
            } else {
                autoSettingComponents = await this.initAutoSettings(defaultComponents);
            }
        }

        this.componentsData = autoSettingComponents.map((x: FormComponent) => ({
            componentName: x.componentName,
            order: x.order,
            active: false,
            activeDropTarget: false,
            collapseState: x.collapseState,
            sectionName: x.sectionName
        }));
    }

    async initAutoSettings(defaultComponents: FormComponent[]) {
        await setSetting(this.componentsAutoSettingKey,
            defaultComponents);
        return getSetting(this.componentsAutoSettingKey);
    }

    async updateComponents(defaultComponents: FormComponent[], components: FormComponent[]) {
        let updateSettings = false;

        const componentsToBeAdded = defaultComponents
            .filter(x => !components.map(x => x.componentName).includes(x.componentName));
        const componentsToBeRemoved = components
            .filter(x => !defaultComponents
                .map(x => x.componentName).includes(x.componentName));
        if (componentsToBeAdded.length > 0) {
            components = components.concat(componentsToBeAdded);
        }
        if (componentsToBeRemoved.length > 0) {
            componentsToBeRemoved.forEach(cr => {
                components = components.filter(x => x.componentName === cr.componentName);
            });
        }

        if (componentsToBeAdded.length > 0 || componentsToBeRemoved.length > 0) {
            components.sort((a, b) => a.order - b.order)
                .forEach((el, index) => {
                    el.order = index;
                });
            updateSettings = true;
        }

        components.forEach(c => {
            const dc = defaultComponents.find(x => x.componentName === c.componentName);
            if (dc && dc.sectionName !== c.sectionName) {
                c.sectionName = dc.sectionName;
                updateSettings = true;
            }
        });

        if (updateSettings) {
            await setSetting(this.componentsAutoSettingKey,
                components);
        }

        return components;
    }

    onAclUpdated(acl: Acl[]) {
        if (!this.group.acl) {
            this.group.acl = [] as Acl[];
        }

        this.group.acl = this.group.acl
            .concat(acl
                .filter(acl => !this.group.acl.map(acl => acl.resourceId)
                    .includes(acl.resourceId)));
    }

    onDetailsUpdated(details: UserGroup) {
        this.group.name = details.name || "";
        this.group.description = details.description || "";
        this.group.azureAdGroupId = details.azureAdGroupId || "";
    }

    onInitUsers(data: string[]) {
        this.localUserGroup.userIds = data;
        this.group.userIds = data;
    }

    onUsersUpdated(users: string[]) {
        this.group.userIds = users;
    }

    onBeforeRouteLeave(to: Route, next: any) {
        if (this.nextRoute === "" && !this.saveClicked) {
            this.childComponentsHandler();
            this.nextRoute = to.name ? to.name : "";
            this.routeParams = to.params;
            this.isEqual(this.group, this.localUserGroup) ? next() : (this.$refs.UnsavedFormModal as any).open();
        } else {
            next();
        }
    }

    isEqual(obj1: any, obj2: any) {
        var numOfEqualProps = 0;
        var numOfKeys = Object.keys(obj1).length;
        for (const [key1, value1] of Object.entries(obj1)) {
            for (const [key2, value2] of Object.entries(obj2)) {
                if (key1 === key2) {
                    if (JSON.stringify(value1) === JSON.stringify(value2)) {
                        numOfEqualProps++;
                    }
                }
            }
        }
        return numOfEqualProps === numOfKeys;
    }

    onContinue(data: boolean) {
        if (data) this.$router.push({ name: this.nextRoute, params: this.routeParams });
        this.nextRoute = "";
    }

    async save(back: boolean) {
        this.saveClicked = true;
        await this.childComponentsHandler();

        if (!this.formState) {
            this.saveClicked = false;
            return;
        }
        if (!this.editMode) {
            createUserGroup(this.group).then((id: string) => {
                if (back) this.$router.push("/UserGroups");
                else {
                    this.localId = id;
                    this.group.id = id;
                    this.localUserGroup.id = id;
                }
            });
        } else {
            updateUserGroup(this.group).then(() => {
                if (back) this.$router.push("/UserGroups");
            });
        }
    }

    fetchData() {
        if (this.editMode) {
            getUserGroupById(this.localId).then(response => {
                this.group = JSON.parse(JSON.stringify(response));
                bus.$emit("documentTitle", this.group.name);
                this.localUserGroup = JSON.parse(JSON.stringify(this.group));
                if (!this.localUserGroup.userIds) this.localUserGroup.userIds = [];
                if (!this.localUserGroup.acl) this.localUserGroup.acl = [];

                getUserGroupAcl(this.localId).then((response: Acl[]) => {
                    this.acl = response;
                    this.localUserGroup.acl = JSON.parse(JSON.stringify(this.acl));
                });
            });
        }
    }

    async childComponentsHandler() {
        const validators = [] as any[];
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            if (component.validators) { validators.push(component.validators); }
            component.emitData();
        });

        this.formState = await validateForm(validators);
    }

    // drag & drop event handlers
    drag(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.target.id);
        if (!component) { return; }

        event.dataTransfer.setDragImage((this.$refs[component.componentName] as any)[0], 1000, 0);
        event.dataTransfer.setData(
            "id",
            event.target.id
        );

        component.active = true;
    }

    dragLeave(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        component.activeDropTarget = false;
    }

    dragOver(event: any) {
        event.preventDefault();
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        if (component.active) return;
        component.activeDropTarget = true;
    }

    dragEnd() {
        this.componentsData.forEach(x => {
            x.activeDropTarget = false;
            x.active = false;
        });
    }

    swapComponents(event: any) {
        event.preventDefault();
        if (!event.currentTarget) {
            return;
        }

        const target = this
            .componentsData
            .find(x => event.currentTarget && x.componentName === event.currentTarget.id);
        const source = this
            .componentsData
            .find(x => event.dataTransfer && x.componentName === event.dataTransfer.getData("id"));

        if (!(source && target)) { return; }

        const sourceOrder = source.order;
        source.order = target.order;
        target.order = sourceOrder;

        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({ componentName: x.componentName, sectionName: x.sectionName, order: x.order, collapseState: x.collapseState })));
    }

    back() {
        window.history.go(-1);
    }

    onComponentStateChanged(componentState: any) {
        const component = this.componentsData.find(x => x.componentName === componentState.name);
        if (!component) return;

        component.collapseState = componentState.state;
        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({ componentName: x.componentName, order: x.order, collapseState: x.collapseState } as FormComponent)));
    }
}
